/**
 * A hundred lines of DOM helpers instead of a framework.
 *
 * The app has one shape of interaction — fetch, render, click a cell — and a framework
 * would be the largest dependency in a repo that currently has almost none. If this
 * grows a second kind of state, revisit; until then the cost of a build toolchain
 * exceeds the cost of writing `el`.
 */

type Child = Node | string | number | null | undefined | false;

/** Create an element. Attributes whose value is null/false/undefined are skipped. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, unknown> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2), value as EventListener);
    } else node.setAttribute(key, value === true ? '' : String(value));
  }
  append(node, children);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/** Replace a node's children. */
export function fill(node: Element, ...children: Child[]): void {
  node.replaceChildren();
  append(node, children);
}

export function frag(...children: Child[]): DocumentFragment {
  const f = document.createDocumentFragment();
  append(f, children);
  return f;
}

/**
 * Relative time, short enough for a table cell.
 *
 * "3d" rather than "3 days ago": in a column of ages the unit is understood from the
 * column, and the shorter form lets the number stay aligned.
 */
export function age(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${String(mins)}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${String(hours)}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${String(days)}d`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${String(months)}mo` : `${String(Math.floor(months / 12))}y`;
}

/**
 * A full timestamp, for a popover where precision is the point.
 *
 * Rendered in UTC, deliberately. Source snapshots are dated, not timed — DPOR's
 * 2026-08-12 file is stored as midnight UTC — and rendering that in a western local
 * zone displays "Aug 11", which tells the user the data is a day older than it is.
 * The frame is named so nobody has to guess which one they are looking at.
 */
export function stamp(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return 'unknown';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
  // Midnight UTC means a date-only observation; a time on it would be invented
  // precision.
  const midnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  if (midnight) return date;
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  });
  return `${date}, ${time} UTC`;
}

/**
 * Format a value for a table cell.
 *
 * Numbers get thousands separators because most of them here are money — a revenue
 * ceiling rendered as `999999` makes the reader count digits to find out whether it is
 * a million.
 */
export function display(value: unknown, datatype: string | undefined): string {
  if (datatype === 'integer' || datatype === 'numeric') {
    const n = Number(value);
    if (Number.isFinite(n)) return n.toLocaleString();
  }
  return String(value);
}

/** Postgres interval text to milliseconds. Only the forms the registry actually uses. */
export function intervalMs(interval: string | null): number | null {
  if (interval === null) return null;
  const m = /^(\d+)\s+(second|minute|hour|day|week|month|year)s?$/.exec(interval.trim());
  if (m === null) return null;
  const n = Number(m[1]);
  const unit = m[2] as string;
  const table: Record<string, number> = {
    second: 1000,
    minute: 60_000,
    hour: 3_600_000,
    day: 86_400_000,
    week: 604_800_000,
    month: 2_592_000_000,
    year: 31_536_000_000,
  };
  return n * (table[unit] ?? 0);
}

export function money(amount: number): string {
  // Sub-cent amounts are real here: an AI column is $0.00275 a record. Showing $0.00
  // would imply free, so small values keep enough places to be non-zero.
  if (amount === 0) return '$0.00';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}
